import { useState } from 'react';
import { RefreshCw, Sparkles, X, ZoomIn } from 'lucide-react';
import { GenModelToggle } from './GenModelToggle';
import { MarkOverlay, boxStyle, type Box, type MarkPanel } from './markZones';
import { modalBackdropCls, modalCardCls, overlayCls } from './overlay';
import { GENERATED_VIEWS, OTHER_DIRECTION, directionLabel } from './viewDirections';
import { useLanguage } from '../../contexts/LanguageContext';
import { PICKED } from './pickedLook';

// The "Régénérer" dialog for one Phase 2 part. The user can describe what to change and,
// on the previous attempt, mark the region(s) to regenerate — only those zones are replaced
// (composited back over the untouched rest), so a local fix stays local. The schematic is
// shown alongside for reference but isn't drawn on.
export function RegenerateModal({
  partName,
  schematic,
  schemas = [],
  previous,
  context = [],
  view,
  onViewChange,
  onZoom,
  onConfirm,
  onCancel,
}: {
  partName: string;
  // The part's schematic (crop) — re-sent as the first image on every regenerate.
  schematic?: string;
  // THE PART'S OTHER DRAWINGS: the crops the schema row found of it in the assembly steps.
  // Ticked by default and droppable one by one, like the context photographs — and for the
  // same reason, which is that they are useful in general and wrong in particular. A step
  // that draws the rail half-hidden behind the seat is a drawing of two things, and the
  // person looking at the render can see that and this dialog cannot.
  //
  // The portrait above is NOT one of these: it is what the part is generated FROM and is
  // never optional. These say what it leaves out.
  schemas?: string[];
  // The current generated image (the attempt to correct), when the column has one.
  previous?: string;
  // The context pictures that will ACTUALLY be sent — the selected ones only. Deselecting
  // one in the table takes it out of here, because this dialog's whole claim is that it
  // shows the call about to be made. Empty for hardware / tools, which are generated with
  // no product context at all.
  context?: { url: string; view?: string }[];
  // The angle the next image is taken from, and its setter. Both undefined in the LLM
  // pipeline, which has no multi-view reconstructor downstream and so no angle to choose;
  // their presence is what puts the selector in the dialog.
  view?: string;
  onViewChange?: (view: string) => void;
  // Open one of the images larger (the shared lightbox).
  onZoom: (url: string) => void;
  onConfirm: (opts: {
    feedback: string;
    includePrevious: boolean;
    previous?: string;
    // Zones drawn on the previous attempt — only these regions are kept from the new render
    // and composited back over the untouched previous image.
    maskBoxes?: Box[];
    // The context pictures still ticked HERE, by url. A second sieve under the table's:
    // the table picks the pool this part is generated from at all, and this drops some of
    // it for one call — an angle that is confusing THIS piece and useful for the next.
    // Undefined is not "none": it means nothing narrowed the table's own selection.
    context?: string[];
    // The part's other drawings still ticked here, by url. Always sent, like `context`.
    schemas?: string[];
  }) => void;
  onCancel: () => void;
}) {
  const { t } = useLanguage();
  const [text, setText] = useState('');
  // Attach the previous image so the model corrects it (default on when there is one).
  const [includePrev, setIncludePrev] = useState(true);
  // Regions to regenerate, drawn on the previous attempt (zero or more).
  const [previousBoxes, setPreviousBoxes] = useState<Box[]>([]);
  // Whether the draw-on-zoom overlay is open (it shows both images at once).
  const [marking, setMarking] = useState(false);
  // Context pictures dropped for THIS generation. Local and short-lived on purpose: the
  // dialog closes and the table's selection is untouched, because narrowing the context
  // for one difficult part is not a statement about the other twelve.
  const [ctxOff, setCtxOff] = useState<Set<string>>(new Set());
  // Step drawings dropped for THIS generation, on the same terms.
  const [schemaOff, setSchemaOff] = useState<Set<string>>(new Set());
  const drop = (set: (f: (s: Set<string>) => Set<string>) => void) => (url: string) =>
    set((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  const toggleCtx = drop(setCtxOff);
  const toggleSchema = drop(setSchemaOff);

  // Whether this call corrects an existing picture or makes a new one — the same test the
  // previous-attempt thumbnail uses. It is the difference between the two things this one
  // dialog does, so it names them: with the box unticked there is nothing to re-generate
  // and nothing to "fix", and every label saying otherwise was describing the other case.
  const correcting = !!previous && includePrev;

  // The images that go into the draw overlay, side by side: the schematic as a read-only
  // reference, and the previous attempt (drawable — the mask goes on it). Clicking either
  // thumbnail opens the same overlay.
  const panels: MarkPanel[] = [
    schematic && { label: t('notice.catalog.schematic'), src: schematic, boxes: [], onBoxes: () => {}, drawable: false },
    includePrev &&
      previous && { label: t('notice.step3.regenPrevious'), src: previous, boxes: previousBoxes, onBoxes: setPreviousBoxes },
  ].filter(Boolean) as MarkPanel[];

  const confirm = () => {
    const opts: Parameters<typeof onConfirm>[0] = {
      feedback: text,
      includePrevious: includePrev,
    };
    if (includePrev && previous) {
      opts.previous = previous;
      // Only the drawn regions are pasted back from the new render; empty = replace it whole.
      if (previousBoxes.length) opts.maskBoxes = previousBoxes;
    }
    // Always sent, even untouched: the caller then uses exactly this list rather than
    // re-deriving it from the table, so what the dialog showed is what gets sent.
    opts.context = context.filter((c) => !ctxOff.has(c.url)).map((c) => c.url);
    opts.schemas = schemas.filter((url) => !schemaOff.has(url));
    onConfirm(opts);
  };

  return (
    <>
      <div className={`${overlayCls} ${modalBackdropCls}`} onClick={onCancel}>
        <div
          className={`flex w-full max-w-md flex-col items-center gap-4 p-5 text-center ${modalCardCls}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Title centered, close button floated in the corner. */}
          <div className="relative w-full">
            <div className="px-6 text-sm font-semibold text-slate-900">
              {t(correcting ? 'notice.step3.regenerate' : 'notice.step3.generateNew')} ·{' '}
              <span className="font-normal">{partName}</span>
            </div>
            <button
              type="button"
              onClick={onCancel}
              aria-label={t('notice.catalog.cancel')}
              className="absolute right-0 top-0 rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {(schematic || previous) && (
            <div className="flex flex-wrap items-start justify-center gap-4">
              {schematic && (
                <MarkThumb
                  label={t('notice.catalog.schematic')}
                  src={schematic}
                  boxes={[]}
                  active={false}
                  onOpen={() => setMarking(true)}
                  onClear={() => {}}
                  clearLabel={t('notice.step3.regenClearBox')}
                />
              )}
              {/* Gone the moment it is unchecked, rather than shown faded. Everything in
                  this row is an image the call will carry, and an excluded attempt is not
                  one — leaving it there greyed made the dialog show one thing that was not
                  being sent, which is the single claim it exists to make. */}
              {previous && includePrev && (
                <MarkThumb
                  label={t('notice.step3.regenPrevious')}
                  src={previous}
                  boxes={previousBoxes}
                  active
                  onOpen={() => setMarking(true)}
                  onClear={() => setPreviousBoxes([])}
                  clearLabel={t('notice.step3.regenClearBox')}
                />
              )}
            </div>
          )}

          {/* The finished-product pictures that ride along, and a last chance to drop one.
              The table already chose which pictures this part CAN be generated from; this
              narrows that for one call, and forgets it when the dialog closes.

              Green frame = going. Click to drop, click again to bring back. The tint is on
              the tile, not on the photograph: half of what these pictures are FOR is the
              product's colour, and washing that in green to say "selected" would spoil the
              one thing the generator reads them for. */}
          <PickRow
            label={t('notice.step3.context')}
            pics={context.map((ci) => ({
              url: ci.url,
              caption: ci.view ? directionLabel(ci.view) : undefined,
              faint: ci.view === OTHER_DIRECTION,
            }))}
            off={ctxOff}
            onToggle={toggleCtx}
            onZoom={onZoom}
          />

          {/* AND THE SAME PART DRAWN ELSEWHERE IN THE MANUAL — the crops the schema row found
              of it in the assembly steps, every one of them, each droppable.

              They are here because the drawing above is one view of a solid object and a
              generator handed one view invents the rest: a rail seen edge-on on the
              supplied-parts page has no width there, so the render gives it whatever width
              it likes. The steps draw the same rail from above and from underneath, for
              free, in a manual we have already read.

              Droppable one at a time for the reason the photographs are: a step that draws
              the part half behind another one is a picture of two things, and only the
              person looking at it knows which. */}
          <PickRow
            label={t('notice.step3.schemas')}
            pics={schemas.map((url) => ({ url }))}
            off={schemaOff}
            onToggle={toggleSchema}
            onZoom={onZoom}
          />

          {/* Whether to attach the previous image for correction. */}
          {previous && (
            <label className="flex cursor-pointer items-center justify-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={includePrev}
                onChange={(e) => setIncludePrev(e.target.checked)}
                className="h-4 w-4 cursor-pointer accent-blue-600"
              />
              {t('notice.step3.regenIncludePrev')}
            </label>
          )}

          {/* Which side the next picture is taken from. It lives here rather than on the
              table because this dialog is the only way one image is generated, and an
              angle set two scrolls away from the button that uses it is an angle that gets
              forgotten. 3D pipeline only — see the prop.

              "None" is not the absence of a choice, it is a choice: it sends no angle at
              all, so the prompt carries no CAMERA paragraph and the generator frames the
              part the way the drawing does — which is what the LLM pipeline has always
              done. It also puts back the "same camera angle as the previous attempt"
              clause a correction needs, and that clause is dropped whenever an angle IS
              asked for (see notice.py). So a fix to the shape of a picture you are happy
              with the framing of is asked for HERE, by naming no angle.

              The picture is then stored with no direction recorded, which is the honest
              label: nobody said which side it is. Phase 5 lists it under "not sent" and
              its own dropdown is where it gets a name if it turns out to deserve one. */}
          {onViewChange && (
            <label className="flex w-full items-center justify-center gap-2 text-sm text-slate-600">
              {t('notice.step3.angle')}
              <select
                value={view ?? 'front'}
                onChange={(e) => onViewChange(e.target.value)}
                className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm text-slate-800"
              >
                <option value="">{t('notice.step3.angleNone')}</option>
                {GENERATED_VIEWS.map((v) => (
                  <option key={v.id} value={v.id}>{v.label}</option>
                ))}
              </select>
            </label>
          )}

          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            placeholder={
              !correcting
                ? t('notice.step3.genPlaceholder')
                : previousBoxes.length
                  ? t('notice.step3.regenPlaceholderBox')
                  : t('notice.step3.regenPlaceholder')
            }
            autoFocus
            className="w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2 text-left text-sm text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none"
          />

          {/* The generation model — the same shared choice as the toggle above the table, so
              changing it here changes it there too (and vice versa). */}
          <GenModelToggle />

          <button
            type="button"
            onClick={confirm}
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-500"
          >
            {correcting ? (
              <RefreshCw className="h-4 w-4" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            {t(correcting ? 'notice.step3.regenerate' : 'notice.step3.generateNew')}
          </button>
        </div>
      </div>

      {/* Draw-on-zoom overlay — both images at once, rendered after the modal so it's on
          top. */}
      {marking && panels.length > 0 && (
        <MarkOverlay panels={panels} onClose={() => setMarking(false)} />
      )}
    </>
  );
}

// A LABELLED ROW OF PICTURES THE CALL WILL CARRY, each one droppable.
//
// Two rows use it and they are the same gesture on two different kinds of evidence — the
// finished product's photographs, and the part's own drawings from the steps. Green frame =
// going; click to drop, click again to bring it back.
//
// The tint is on the TILE, never on the picture. Half of what a product photograph is for is
// the product's colour, and washing that in green to say "selected" spoils the one thing the
// generator reads it for.
//
// Nothing at all when there is nothing to show: an empty row with a heading is a section
// saying it has nothing to say.
function PickRow({
  label, pics, off, onToggle, onZoom,
}: {
  label: string;
  // `faint` for a caption that is a fallback rather than an answer — an angle nobody named.
  pics: { url: string; caption?: string; faint?: boolean }[];
  off: Set<string>;
  onToggle: (url: string) => void;
  onZoom: (url: string) => void;
}) {
  const { t } = useLanguage();
  if (!pics.length) return null;
  return (
    <div className="w-full">
      <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">
        {label}
      </div>
      <div className="flex flex-wrap items-start justify-center gap-2">
        {pics.map((pic) => {
          const on = !off.has(pic.url);
          return (
            <figure key={pic.url} className="flex flex-col items-center gap-0.5">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => onToggle(pic.url)}
                  aria-pressed={on}
                  title={t(on ? 'notice.step3.contextOn' : 'notice.step3.contextOff')}
                  className={`flex h-14 w-14 items-center justify-center overflow-hidden rounded-lg border-2 transition-all ${
                    on
                      ? PICKED
                      : 'border-slate-200 bg-white opacity-40 hover:opacity-70'
                  }`}
                >
                  <img src={pic.url} alt="" className="max-h-full max-w-full object-contain p-0.5" />
                </button>
                <button
                  type="button"
                  onClick={() => onZoom(pic.url)}
                  title={t('notice.pdf.zoom')}
                  aria-label={t('notice.pdf.zoom')}
                  className="absolute -right-1 -top-1 rounded-md border border-slate-300 bg-white/90 px-1 py-0.5 text-slate-500 shadow-sm backdrop-blur transition-colors hover:border-blue-500 hover:text-blue-600"
                >
                  <ZoomIn className="h-3 w-3" />
                </button>
              </div>
              {pic.caption && (
                <figcaption
                  className={`max-w-14 truncate text-[9px] font-semibold uppercase leading-tight tracking-wide transition-opacity ${
                    on ? '' : 'opacity-40'
                  } ${
                    pic.faint
                      ? 'text-slate-400'
                      : 'text-blue-700'
                  }`}
                >
                  {pic.caption}
                </figcaption>
              )}
            </figure>
          );
        })}
      </div>
    </div>
  );
}

// A labelled reference image showing its target zones, click to draw/edit them large. When
// inactive (e.g. the previous image is excluded) it just shows the picture.
function MarkThumb({
  label,
  src,
  boxes,
  active,
  onOpen,
  onClear,
  clearLabel,
}: {
  label: string;
  src: string;
  boxes: Box[];
  active: boolean;
  onOpen: () => void;
  onClear: () => void;
  clearLabel: string;
}) {
  return (
    <figure className="flex flex-col items-center gap-1.5">
      <figcaption className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
        {label}
      </figcaption>
      <div
        onClick={onOpen}
        className="relative cursor-zoom-in overflow-hidden rounded-lg border border-slate-200 bg-white"
      >
        <img src={src} alt="" className="block max-h-44 w-auto object-contain" />
        {active &&
          boxes.map((b, i) => (
            <div
              key={i}
              className="pointer-events-none absolute border-2 border-red-500 bg-red-500/10"
              style={boxStyle(b)}
            />
          ))}
      </div>
      {active && boxes.length > 0 && (
        <button
          type="button"
          onClick={onClear}
          className="text-[11px] text-slate-400 underline decoration-slate-400/50 underline-offset-2 hover:text-slate-600"
        >
          {clearLabel}
        </button>
      )}
    </figure>
  );
}

